import * as React from "react";
import * as moment from "moment";
import {IReduxState} from "reducers";
import {connect} from "react-redux";
import {ILoanApprovalInfo, IUserStatus} from "StateInterface";
import {hideConsentLoader, showConsentLoader} from "consent/actions";
import ".././styles.scss";
import {handleConsentNavigation, RoutePaths} from "consent/route";
import MoratoriumProductTable from "./MoratoriumProductTable/index";
import RoundedButton from "components/roundedButton";
import ConsentBox from "consent/utils/consentBox";
import {imageBaseUrl} from "config";
import {getLenderName} from "utils/lenderDetails";
import {DocType, downloadPdf, getBase64FromUrl, getPreSignedUrlDoc, markConsent, Type} from "consent/api";
import {EventName, updateConsentEvent} from "consent/events";
import {history} from "load/store";
import {partnerOfferings} from "login/config";
import {getLatestLoanOpp} from "consent/actions";

interface IConnectProps {
    config: any;
    loading: boolean;
    userStatus: IUserStatus;
    userId: number;
    loanApprovalInfo: ILoanApprovalInfo;
    lenderOpportunityId: string;
    isAndroid: boolean;
    dispatch?: (action: any) => void;
    utm_source: string;
}

const moratoriumPeriod = "3 months";
const noOfMoratoriumMonths = 3;

// write productcode in lowercase
const product = {
    RESTRUCTURED_LOAN : "restructured_loan",
    MORATORIUM_LOAN : "morat",
    RLOC: "rloc",
    FINO: "rloc::fino_bank",
    SPICEMONEY: "rloc::spicemoney",
    EASYPAY: "rloc::easypay",
    FINO_DISTRIBUTOR: "rloc::fino_distributor",
    PAYWORLD: "rloc::payworld",
    AIRTEL_DISTRIBUTOR: "rloc::airtel_distributor"
};
const tooltipText = {
    validity: "The duration for which you credit line will be active",
    convenienceFee: "Fee charged for every withdrawal. Total fee available in monthly bill",
    processingFee: "Only one time during the first bill generated",
    billGenDate: "Fixed day of the month when bill is generated with amount withdrawn + interest + applicable fees",
    dueDate: "The last day to pay billed amount",
    interestRate: "Reducing Balance Method",
};
const productRepaymentText = [product.FINO, product.EASYPAY, product.SPICEMONEY, product.FINO_DISTRIBUTOR, product.PAYWORLD, product.AIRTEL_DISTRIBUTOR];

class LoanDetailsForConsent extends React.Component<IConnectProps, any> {
    constructor(props) {
        super(props);
        this.handleAcceptOffer = this.handleAcceptOffer.bind(this);
        this.handleDeclineOffer = this.handleDeclineOffer.bind(this);
        this.handleDownload = this.handleDownload.bind(this);
        this.state = {
            isConsentChecked: true,
            downloadDocMsg: {
                isDownloaded: false,
                msg: "",
                error: false,
            }
        }
    }

    public componentDidMount() {
        if (!this.props.loanApprovalInfo && this.props.lenderOpportunityId) {
            this.props.dispatch(getLatestLoanOpp(this.props.lenderOpportunityId));
        }
    }

    public handleAcceptOffer() {
        this.props.dispatch(showConsentLoader());
        markConsent(this.props.lenderOpportunityId, true, null)
        .then((res) => {
            updateConsentEvent(EventName.ACCEPT_LOAN_OFFER, true);
            this.props.dispatch(hideConsentLoader());
            if (res.status === 200) {
                this.props.dispatch(getLatestLoanOpp(this.props.lenderOpportunityId));
            }
        })
        .catch((err) => {
            if (err.response.status === 400 &&  err.response.data === "Consent is already marked") {
                history.replace({pathname: "/"});
            }
        });
    }

    public handleDeclineOffer() {
        handleConsentNavigation(RoutePaths.CONSENT_DENIED, false);
    }

    public renderLoanApprovalDetail(title: string, value: string, tooltipText?: string) {
        return (
            <div className="loan-details">
                <div>
                    {title}
                    {
                        tooltipText &&
                        <div className="tooltip-container">?<span className="tooltipText">{tooltipText}</span></div>
                    }
                </div>
                <div className="loan-details-desc">
                    {value}
                </div>
            </div>);
    }
    public getLoanApprovalDetail() {
       return this.props.loanApprovalInfo.productCode
         .toLowerCase()
         .includes(product.MORATORIUM_LOAN)
         ? this.getMoratoriumFlatInterest(
             this.props.loanApprovalInfo.amount,
             this.props.loanApprovalInfo.emiAmount,
             this.props.loanApprovalInfo.tenureMonths,
           )
         : this.props.loanApprovalInfo.productCode
             .toLowerCase()
             .includes(product.RLOC)
         ? this.getRlocFlatInterest(
             this.props.loanApprovalInfo.interestPercent
           )
         : this.getFlatInterest(
              this.props.loanApprovalInfo.interestPercent,
            );
    }

    public getFlatInterest(interestRate: number) {
        const flatInterest = ((interestRate / 12).toFixed(2));
        return Number(flatInterest);
    }

    public getMoratoriumFlatInterest(loanAmount, emi, tenure) {
        const repaymentAmount = (Math.round(emi) * (tenure - noOfMoratoriumMonths));
        const flatInterest = (((repaymentAmount / loanAmount) - 1) / (tenure)) * 100;
        return flatInterest.toFixed(2);
    }
    public getRlocFlatInterest(roi: number) {
        return Number((roi / 12).toFixed(2));
    }
    public getCovenienceFee () {
       if (this.props.loanApprovalInfo?.convenienceFeePct) {
         return `${this.props.loanApprovalInfo?.convenienceFeePct}% (min ₹ ${this.props.loanApprovalInfo?.minConvenienceFee || 0}) + GST`;
       }
       return `₹ ${Math.round((this.props.loanApprovalInfo?.convenienceFee || 0))} + GST`;
    }

    public async handleDownload() {
        try {
            const dataUrl = await getPreSignedUrlDoc(this.props.userId, DocType.KFS, this.props.lenderOpportunityId);
            const base64 = await getBase64FromUrl(dataUrl);
            const b64EncodeString = JSON.stringify(base64).split("base64,")[1].slice(0, -1);
            const decodeB64 = window.atob(b64EncodeString);
            downloadPdf(Type.BS64, decodeB64, DocType.KFS);
            this.setState({downloadDocMsg: {
                isDownloaded: true,
                msg: "Your KFS has been Successfully downloaded!",
                error: false,
            }});
        } catch (error) {
            this.setState({downloadDocMsg: {
                isDownloaded: true,
                msg: "Something went wrong please try again later",
                error: true,
            }});
        }
    }

    public render() {
        if (this.props.loanApprovalInfo) {
            const loanAmount = `${this.props.loanApprovalInfo.amount}`.replace(/(\d)(?=(\d\d)+\d$)/g, "$1,");
            const productCode = this.props.loanApprovalInfo.productCode.toLowerCase();
            const lenderImageName = getLenderName(this.props.loanApprovalInfo.lenderId);
            const loanSummaryStyle = {
              minHeight: this.props.isAndroid ? "calc(100vh - 100px)" : window.screen.width < 451 ? "calc(100vh - 225px)" : "100%",
            };
            const lenderImageStyle = {
                marginTop: 10,
                marginBottom: this.props.isAndroid || window.screen.width < 451 ? 0 : -22,
            };
            const isRloc = productCode.includes(product.RLOC);
            return (
                <ConsentBox>
                    <div className="loan-summary" style={loanSummaryStyle}>
                        <div style={{paddingTop: 19, fontSize: 14}}>
                            <div style={{textAlign: "center", letterSpacing: 0.24}}>
                                <span>Your {productCode === product.RESTRUCTURED_LOAN && "restructured"} {isRloc ? "credit line of" : "loan"} <span
                                    style={{fontWeight: 900}}>
                                    ₹{loanAmount}</span> has been approved by {this.props.loanApprovalInfo.lenderId && getLenderName(this.props.loanApprovalInfo.lenderId).toUpperCase()}</span>
                            </div>
                            <div style={{letterSpacing: 0, marginTop: 24}}>
                                {productCode.includes(product.MORATORIUM_LOAN) ?
                                    this.renderLoanApprovalDetail("Approximate disbursal day", moment(new Date()).format("DD/MMMM/YYYY"))
                                    : null}
                                {!productCode.includes(product.FINO) && this.renderLoanApprovalDetail( `${isRloc ? "Validity" : "Loan tenure"}${productCode === product.RESTRUCTURED_LOAN ? " (Including 3 months moratorium)" : ""}`, `${this.props.loanApprovalInfo
                                    ? this.props.loanApprovalInfo.tenureMonths : null} Months`, isRloc ? tooltipText.validity : null)}
                                {this.renderLoanApprovalDetail("Interest Rate", `${
                                    this.getLoanApprovalDetail()
                                } % per month (Annual ${Math.floor(Number(this.getLoanApprovalDetail()) * 12)}%)`, tooltipText.interestRate)}
                                { !isRloc && this.renderLoanApprovalDetail("EMI:",
                                    `₹ ${`${Math.round(this.props.loanApprovalInfo.emiAmount)}`.replace(/(\d)(?=(\d\d)+\d$)/g, "$1,")} / month`)}
                                {productCode.includes(product.MORATORIUM_LOAN) ?
                                    this.renderLoanApprovalDetail("Moratorium periods:", moratoriumPeriod)
                                    : null}
                                {this.renderLoanApprovalDetail("Processing Fees", `${`₹ ${Math.round(this.props.loanApprovalInfo.processingFee)} + GST`.replace(/(\d)(?=(\d\d)+\d$)/g, "$1,")}`, isRloc ? tooltipText.processingFee : null)}
                                {productCode !== product.RESTRUCTURED_LOAN && !productCode.includes(product.RLOC) && this.renderLoanApprovalDetail("Total Repayment:",
                                    `${`₹ ${Math.round(this.props.loanApprovalInfo.emiAmount)
                                    * (productCode.includes(product.MORATORIUM_LOAN) ? this.props.loanApprovalInfo.tenureMonths - noOfMoratoriumMonths : this.props.loanApprovalInfo.tenureMonths)}`.replace(/(\d)(?=(\d\d)+\d$)/g, "$1,")}`)
                                }
                                {
                                    !productCode.includes(product.RLOC) && this.props.loanApprovalInfo.dueDayOfMonth &&
                                    this.renderLoanApprovalDetail("EMI Due Day:", `${this.props.loanApprovalInfo.dueDayOfMonth}th of Every month`)
                                }
                                {
                                 productCode.includes(product.RLOC) &&
                                 <>
                                     {this.renderLoanApprovalDetail(`Convenience Fees`, `${this.getCovenienceFee()}`, tooltipText.convenienceFee)}
                                     {productCode !== product.PAYWORLD && this.renderLoanApprovalDetail("Interest Billing", "1st of every month", tooltipText.billGenDate)}
                                     {productCode !== product.PAYWORLD && this.renderLoanApprovalDetail("Interest Bill Due", "5th of every month", tooltipText.dueDate)}
                                     {this.renderLoanApprovalDetail("Principal Repayment", productRepaymentText.includes(productCode) ? "Daily" : "5th of every month")}
                                 </>
                                }
                            </div>
                        </div>
                        <div className="doc-container">
                            <p className="consent-doc-text">Please find Key Fact Statement for your loan below!</p>
                            <RoundedButton
                                buttonText="Download Here"
                                bgColor="#fff"
                                size="small"
                                borderColor={partnerOfferings[this.props.utm_source]?.colors?.green ?
                                    partnerOfferings[this.props.utm_source]?.colors?.green
                                        : "#F5821F"
                                    }
                                textColor={partnerOfferings[this.props.utm_source]?.colors?.green ?
                                partnerOfferings[this.props.utm_source]?.colors?.green
                                    : "#F5821F"
                                }
                                icon={`${imageBaseUrl}/img/download-here.svg`}
                                onClick={this.handleDownload}
                            />
                            {this.state.downloadDocMsg.isDownloaded && <p className={`msg ${this.state.downloadDocMsg.error ? "warningMsg" : "successMsg"}`}>
                                    {this.state.downloadDocMsg.msg}
                                </p>}
                        </div>
                        <div className="consent-check-Box">
                            <input 
                            type="checkbox" 
                            defaultChecked={this.state.isConsentChecked}
                            onChange={() => {this.setState({isConsentChecked:!this.state.isConsentChecked})}}
                            className="check-box-input"
                            />
                            <div className="consent-declaration">I hereby give a declaration that my Family income 
                            (Family includes spouse and unmarried children) is more than INR 3 lakhs.
                            </div>
                        </div>
                        {!this.state.isConsentChecked && <div className="consent-declaration-error">*Mandatory field.</div>}
                        <div style={{display: "flex", justifyContent: "center", marginTop: 18}}>
                            <RoundedButton
                                bgColor={partnerOfferings[this.props.utm_source]?.colors?.green}
                                buttonText={`Accept ${isRloc ? "credit line" : "loan"} offer`}
                                onClick={() => this.handleAcceptOffer()}
                                size="large"
                                isDisabled={!this.state.isConsentChecked}
                            />
                        </div>
                        <div className="textButton" style={{
                            letterSpacing: 0.24,
                            color: "#898989",
                            textAlign: "center",
                            marginTop: 12,
                            fontSize: 10,
                        }} onClick={() => this.handleDeclineOffer()}>
                            {` I don't accept this ${isRloc ? "credit line" : "loan"} offer`}
                        </div>
                        {productCode.includes(product.MORATORIUM_LOAN) ?
                            <div style={{marginTop: 24, marginBottom: 50}}>
                                <MoratoriumProductTable emi={Math.round(this.props.loanApprovalInfo.emiAmount)}
                                                        tenure={this.props.loanApprovalInfo.tenureMonths}/>
                            </div> : null}
                    </div>
                    <div className="flex-column-center-row" style={lenderImageStyle}>
                        <div style={{fontSize: 15}}>
                            Loan Provided By
                        </div>
                        <div>
                            <img style={{width: 150, height: "auto", marginTop: 4}}
                                 src={`${imageBaseUrl}/img/lender_logo/${lenderImageName}.png`} alt={lenderImageName}/>
                        </div>
                    </div>
                </ConsentBox>
            );
        } else {
            return null;
        }
    }
}

const mapStateToProps = (state: IReduxState) => {
    return {
        userStatus: state.userform.userStatus,
        config: state.userform.config,
        loading: state.consent.loading,
        userId: state.login.auth.userId,
        loanApprovalInfo: state.consent.loanApprovalInfo,
        lenderOpportunityId: state.consent.lenderOpportunityId,
        isAndroid: state.login.isAndroid,
        utm_source: state.login.authExt ? state.login.authExt.utm_source : null,
    };
};
export default connect<IConnectProps, {}, {}>(mapStateToProps)(LoanDetailsForConsent);
